import { Component, EventEmitter, Input, OnChanges, Output, ChangeDetectionStrategy } from '@angular/core'
import { laskeTekstinMuutokset, TekstinMuutoksenTyyppi, TekstinMuutos } from '@lemon/core/utils/teksti-diff'
import { TilikarttaRaja } from '../../_jaettu-lemonator/model/llm-kirjausehdotus-testaus'

export interface VertailtavaPromptiversio {
  versio: number
  paivitetty: string
  teksti: string
  nimi?: string
  tilikarttaRajat?: TilikarttaRaja[]
}

export interface PromptiversioVertailu {
  valittu: VertailtavaPromptiversio
  edellinen: VertailtavaPromptiversio | null
  aktiivinen: VertailtavaPromptiversio | null
}

export type Vertailukohta = 'edellinen' | 'aktiivinen'

interface TilisarjanMuutos {
  raja: TilikarttaRaja
  tyyppi: TekstinMuutoksenTyyppi
}

export function luoPromptiversioVertailu<T extends { versio: number }>(
  valittu: T,
  historia: T[],
  aktiivinen: T | null | undefined,
  muunna: (rivi: T) => VertailtavaPromptiversio,
): PromptiversioVertailu {
  let edellinen: T | null = null
  for (const rivi of historia) {
    if (rivi.versio < valittu.versio && (!edellinen || rivi.versio > edellinen.versio)) {
      edellinen = rivi
    }
  }
  return {
    valittu: muunna(valittu),
    edellinen: edellinen ? muunna(edellinen) : null,
    aktiivinen: aktiivinen ? muunna(aktiivinen) : null,
  }
}

@Component({
  selector: 'app-prompti-versiovertailu',
  templateUrl: './prompti-versiovertailu.component.html',
  styleUrls: ['./prompti-versiovertailu.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PromptiVersiovertailuComponent implements OnChanges {
  @Input({ required: true }) vertailu!: PromptiversioVertailu

  @Output() palauta = new EventEmitter<number>()
  @Output() sulje = new EventEmitter<void>()

  vertailukohta: Vertailukohta = 'edellinen'
  vertailuversio: VertailtavaPromptiversio | null = null
  muutokset: TekstinMuutos[] = []
  onMuutoksia = false
  nimiMuuttui = false
  tilisarjojenMuutokset: TilisarjanMuutos[] = []

  ngOnChanges() {
    this._laske()
  }

  asetaVertailukohta(vertailukohta: Vertailukohta) {
    this.vertailukohta = vertailukohta
    this._laske()
  }

  private _laske() {
    const { valittu, edellinen, aktiivinen } = this.vertailu
    this.vertailuversio = this.vertailukohta === 'edellinen' ? edellinen : aktiivinen
    const vertailuversio = this.vertailuversio

    this.muutokset = vertailuversio ? laskeTekstinMuutokset(vertailuversio.teksti, valittu.teksti) : [{ teksti: valittu.teksti, tyyppi: 'ennallaan' }]
    this.onMuutoksia = this.muutokset.some(muutos => muutos.tyyppi !== 'ennallaan')
    this.nimiMuuttui = !!vertailuversio && vertailuversio.nimi !== valittu.nimi
    this.tilisarjojenMuutokset = this._laskeTilisarjojenMuutokset(vertailuversio?.tilikarttaRajat ?? valittu.tilikarttaRajat ?? [], valittu.tilikarttaRajat ?? [])
  }

  private _laskeTilisarjojenMuutokset(vanhat: TilikarttaRaja[], uudet: TilikarttaRaja[]) {
    const avain = (raja: TilikarttaRaja) => `${raja.alkaa}-${raja.loppuu}`
    const uudetAvaimet = new Set(uudet.map(avain))
    const vanhatAvaimet = new Set(vanhat.map(avain))
    const muutokset: TilisarjanMuutos[] = vanhat.map(raja => ({ raja, tyyppi: uudetAvaimet.has(avain(raja)) ? 'ennallaan' : 'poistettu' }))
    for (const raja of uudet) {
      if (!vanhatAvaimet.has(avain(raja))) {
        muutokset.push({ raja, tyyppi: 'lisatty' })
      }
    }
    return muutokset
  }
}
